import React from "react"

interface PrefixedInputProps {
  id: string
  value: string
  placeholder: string
  prefix?: string
  prefixOptions?: { value: string; label: string; default?: boolean }[]
  selectedPrefix?: string
  onChange: (value: string) => void
  onPrefixChange?: (prefix: string) => void
}

// Common input classes to prevent Chrome autofill styling
const inputBaseClasses =
  "bg-white text-gray-900 border-0 outline-none [&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_white] [&:-webkit-autofill]:[-webkit-text-fill-color:#111827]"

/**
 * Text input with optional static or selectable prefix
 */
const PrefixedInput: React.FC<PrefixedInputProps> = ({
  id,
  value,
  placeholder,
  prefix,
  prefixOptions,
  selectedPrefix,
  onChange,
  onPrefixChange,
}) => {
  const hasStaticPrefix = !!prefix
  const hasPrefixDropdown = prefixOptions && prefixOptions.length > 0

  const currentPrefix =
    selectedPrefix ||
    prefixOptions?.find(opt => opt.default)?.value ||
    prefixOptions?.[0]?.value ||
    ""

  if (hasStaticPrefix) {
    return (
      <div className="flex w-full rounded-lg">
        <span className="inline-flex items-center px-3 text-sm text-gray-500 bg-white rounded-l-lg font-mono border-0 outline-none">
          {prefix}
        </span>
        <input
          id={id}
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          className={`flex-1 p-3 rounded-r-lg border-l border-l-gray-300 ${inputBaseClasses}`}
          placeholder={placeholder}
        />
      </div>
    )
  }

  if (hasPrefixDropdown) {
    return (
      <div className="flex w-full rounded-lg">
        <select
          value={currentPrefix}
          onChange={e => onPrefixChange?.(e.target.value)}
          className="inline-flex w-[90px] items-center px-3 text-sm text-gray-700 bg-white rounded-l-lg font-mono cursor-pointer border-0 outline-none"
        >
          {prefixOptions?.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <input
          id={id}
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          className={`flex-1 p-3 rounded-r-lg border-l border-l-gray-300 ${inputBaseClasses}`}
          placeholder={placeholder}
        />
      </div>
    )
  }

  return (
    <input
      id={id}
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      className={`w-full p-3 rounded-lg border-l border-l-gray-300 ${inputBaseClasses}`}
      placeholder={placeholder}
    />
  )
}

export default PrefixedInput
